import React, { useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Activity,
  Baby,
  Calendar,
  ClipboardList,
  CreditCard,
  FileText,
  FlaskConical,
  Heart,
  MessageSquare,
  Pill,
  Send,
  Stethoscope,
  Syringe,
  TestTubes,
  User,
  Users,
} from "lucide-react";

import { useUserRole } from "components/Sidebar/hooks/useUserRole";
import { TabSwitcher } from "components/ui/TabSwitcher";
import { useCurrentPatientId } from "features/patients/hooks/useCurrentPatientId";
import { usePracticeSettings } from "features/practice/hooks/usePracticeSettings";
import { pathGenerators } from "utils/routes";
import { isTenantTypeAllowed, TENANT_TYPES } from "utils/tenantAccess";

const iconProps = { size: 20, strokeWidth: 2 };

// `to` builds the tab path from the current patient id; `key` is the stable tab
// identity used for active-tab matching (the id-independent trailing segment).
// `tenantTypes` mirrors the sidebar's menuConfig convention: omitted/empty =
// visible to every tenant type; GP-only tabs list `[TENANT_TYPES.GP]`.
const menuItems = [
  { icon: User, label: "Details", key: "details", to: pathGenerators.patient.details },
  { icon: Stethoscope, label: "Consult", key: "consult", to: pathGenerators.patient.consult },
  { icon: Calendar, label: "Consultations", key: "consultations", to: pathGenerators.patient.consultations },
  { icon: Pill, label: "Medications", key: "medication", to: pathGenerators.patient.medication },
  { icon: Activity, label: "Baseline", key: "baseline", to: pathGenerators.patient.baseline },
  { icon: FileText, label: "Documents", key: "documents", to: pathGenerators.patient.documents },
  { icon: FlaskConical, label: "Results", key: "results", to: pathGenerators.patient.results, tenantTypes: [TENANT_TYPES.GP] },
  { icon: Send, label: "Referrals", key: "referrals", to: pathGenerators.patient.referrals, tenantTypes: [TENANT_TYPES.GP] },
  { icon: TestTubes, label: "eLabs", key: "labOrders", to: pathGenerators.patient.labOrders, tenantTypes: [TENANT_TYPES.GP] },
  { icon: Syringe, label: "Immunisations", key: "immunizations", to: pathGenerators.patient.immunizations, tenantTypes: [TENANT_TYPES.GP] },
  { icon: Heart, label: "Chronic Disease", key: "cdm", to: pathGenerators.patient.cdm, tenantTypes: [TENANT_TYPES.GP] },
  { icon: Baby, label: "Pregnancy", key: "pregnancy", to: pathGenerators.patient.pregnancy, tenantTypes: [TENANT_TYPES.GP] },
  { icon: Users, label: "Family", key: "family", to: pathGenerators.patient.family, tenantTypes: [TENANT_TYPES.GP] },
  { icon: MessageSquare, label: "Communications", key: "communication", to: pathGenerators.patient.communication },
  { icon: CreditCard, label: "Payments", key: "payments", to: pathGenerators.patient.payments },
  { icon: ClipboardList, label: "Tasks", key: "tasks", to: pathGenerators.patient.tasks },
];

const HEALTHLINK_HIDDEN_KEYS = ["results", "referrals", "labOrders", "cdm"];

// Medications is shown to every practice by default and is only hidden for a
// CONSULT practice that has explicitly turned the feature off in Practice
// Settings (practice_settings.medications, see z_219) — GP practices always
// see it regardless of this setting, unlike the tenantTypes-gated tabs above.
const CONSULT_SETTINGS_GATED_KEYS = { medication: "medications" };

const NavigationBar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const patientId = useCurrentPatientId();
  const { data: practiceSettings } = usePracticeSettings();
  const { tenantType } = useUserRole();

  const tabOptions = useMemo(() => {
    let items = menuItems;
    if (practiceSettings?.healthLink === false) {
      items = items.filter((item) => !HEALTHLINK_HIDDEN_KEYS.includes(item.key));
    }
    if (tenantType?.toUpperCase() === TENANT_TYPES.CONSULT) {
      items = items.filter((item) => {
        const settingKey = CONSULT_SETTINGS_GATED_KEYS[item.key];
        return !settingKey || practiceSettings?.[settingKey] !== false;
      });
    }
    items = items.filter((item) =>
      isTenantTypeAllowed(item.tenantTypes, tenantType)
    );
    return items.map((item) => {
      const IconComponent = item.icon;
      return {
        value: item.key,
        label: item.label,
        icon: <IconComponent {...iconProps} />,
      };
    });
  }, [practiceSettings, tenantType]);

  const currentValue = useMemo(() => {
    const path = location.pathname;
    // The consultation editor lives under .../consultation/edit/:id — keep the
    // Consultations tab active for it.
    if (path.includes("/consultation/")) return "consultations";

    const match = menuItems.find(
      (item) => patientId && item.to(patientId) === path
    );

    return match?.key ?? menuItems[0].key;
  }, [location.pathname, patientId]);

  return (
    <div data-testid="patient-navigation">
      <TabSwitcher
        options={tabOptions}
        value={currentValue}
        onChange={(key) => {
          const item = menuItems.find((menuItem) => menuItem.key === key);
          if (item && patientId) navigate(item.to(patientId));
        }}
        fullWidth
        withOverflow
        marginTop={10}
        marginBottom={24}
      />
    </div>
  );
};

export { NavigationBar };
